import axios from "axios";

/**
 * RC-WEB-6 PHASE 3 — single API error normalizer.
 *
 * FastAPI/Pydantic answers a 422 with `detail` as an ARRAY of objects
 * ({type, loc, msg, input, url}). Rendering that straight into JSX (or into
 * `toast.error`) crashes React with "Objects are not valid as a React child".
 *
 * Everything that reaches the UI goes through here, so a component can only
 * ever receive a plain localized string. `input` is NEVER surfaced (it can hold
 * a password or other user content).
 */

const SUPPORTED = ["es", "en", "pt", "fr"];

const MSG = {
  es: {
    form: "Revisa los datos del formulario.",
    network: "Sin conexión con AvisoCar. Revisa tu internet e inténtalo de nuevo.",
    401: "Tu sesión expiró. Vuelve a iniciar sesión.",
    403: "No tienes permiso para hacer esto.",
    404: "No encontramos lo que buscas.",
    409: "Esto ya estaba hecho o está en proceso.",
    413: "El archivo es demasiado grande.",
    429: "Demasiados intentos. Espera un momento e inténtalo otra vez.",
    500: "Tuvimos un problema al procesar tu solicitud. Inténtalo de nuevo.",
    503: "El servicio no está disponible en este momento. Inténtalo en unos minutos.",
    unknown: "Algo no salió bien. Inténtalo de nuevo.",
  },
  en: {
    form: "Please check the form fields.",
    network: "No connection to AvisoCar. Check your internet and try again.",
    401: "Your session expired. Please sign in again.",
    403: "You don't have permission to do this.",
    404: "We couldn't find what you're looking for.",
    409: "This was already done or is in progress.",
    413: "The file is too large.",
    429: "Too many attempts. Wait a moment and try again.",
    500: "We had a problem processing your request. Please try again.",
    503: "The service is unavailable right now. Try again in a few minutes.",
    unknown: "Something went wrong. Please try again.",
  },
  pt: {
    form: "Confira os dados do formulário.",
    network: "Sem conexão com o AvisoCar. Verifique a internet e tente de novo.",
    401: "Sua sessão expirou. Entre novamente.",
    403: "Você não tem permissão para fazer isso.",
    404: "Não encontramos o que você procura.",
    409: "Isso já foi feito ou está em andamento.",
    413: "O arquivo é muito grande.",
    429: "Muitas tentativas. Aguarde um momento e tente de novo.",
    500: "Tivemos um problema ao processar sua solicitação. Tente de novo.",
    503: "O serviço está indisponível agora. Tente em alguns minutos.",
    unknown: "Algo deu errado. Tente de novo.",
  },
  fr: {
    form: "Vérifiez les champs du formulaire.",
    network: "Pas de connexion à AvisoCar. Vérifiez votre internet et réessayez.",
    401: "Votre session a expiré. Reconnectez-vous.",
    403: "Vous n'avez pas la permission de faire cela.",
    404: "Nous n'avons pas trouvé ce que vous cherchez.",
    409: "C'était déjà fait ou c'est en cours.",
    413: "Le fichier est trop volumineux.",
    429: "Trop de tentatives. Patientez un instant et réessayez.",
    500: "Un problème est survenu lors du traitement. Réessayez.",
    503: "Le service est indisponible pour le moment. Réessayez dans quelques minutes.",
    unknown: "Une erreur est survenue. Réessayez.",
  },
};

const FIELD_LABELS = {
  es: { name: "Nombre", age: "Edad", tutor_name: "Nombre de papá o mamá", pet_type: "Tipo de mascota",
    vet_name: "Veterinario", object_type: "Tipo de objeto", description: "Descripción",
    plate_number: "Placa", email: "Correo", password: "Contraseña", full_name: "Nombre completo",
    phone: "Teléfono", plan_months: "Plan", method: "Forma de pago", voucher: "Comprobante" },
  en: { name: "Name", age: "Age", tutor_name: "Parent name", pet_type: "Pet type",
    vet_name: "Vet", object_type: "Object type", description: "Description",
    plate_number: "Plate", email: "Email", password: "Password", full_name: "Full name",
    phone: "Phone", plan_months: "Plan", method: "Payment method", voucher: "Receipt" },
  pt: { name: "Nome", age: "Idade", tutor_name: "Nome do responsável", pet_type: "Tipo de animal",
    vet_name: "Veterinário", object_type: "Tipo de objeto", description: "Descrição",
    plate_number: "Placa", email: "E-mail", password: "Senha", full_name: "Nome completo",
    phone: "Telefone", plan_months: "Plano", method: "Forma de pagamento", voucher: "Comprovante" },
  fr: { name: "Nom", age: "Âge", tutor_name: "Nom du parent", pet_type: "Type d'animal",
    vet_name: "Vétérinaire", object_type: "Type d'objet", description: "Description",
    plate_number: "Plaque", email: "E-mail", password: "Mot de passe", full_name: "Nom complet",
    phone: "Téléphone", plan_months: "Formule", method: "Moyen de paiement", voucher: "Justificatif" },
};

const currentLang = () => {
  try {
    const stored = localStorage.getItem("avisocar_lang") || localStorage.getItem("avisocar_account_lang");
    if (SUPPORTED.includes(stored)) return stored;
  } catch (_e) { /* SSR / privacy mode */ }
  return "es";
};

const fieldName = (loc) => {
  if (!Array.isArray(loc)) return "";
  const parts = loc.filter((p) => p !== "body" && p !== "query" && typeof p === "string");
  return parts.length ? parts[parts.length - 1] : "";
};

const genericFor = (status, lang) => {
  const table = MSG[lang] || MSG.es;
  if (!status) return table.network;
  if (table[status]) return table[status];
  if (status >= 500) return table[500];
  if (status === 422 || status === 400) return table.form;
  return table.unknown;
};

/**
 * @returns {{status:number|null, message:string, fields:Object<string,string>}}
 */
export const parseApiError = (error) => {
  const lang = currentLang();
  const labels = FIELD_LABELS[lang] || FIELD_LABELS.es;
  const status = error?.response?.status ?? null;
  const data = error?.response?.data;
  const fields = {};

  if (!error?.response) {
    return { status: null, message: genericFor(null, lang), fields };
  }

  const detail = data && typeof data === "object" ? data.detail : undefined;

  if (typeof detail === "string" && detail.trim()) {
    return { status, message: detail.trim(), fields };
  }

  if (Array.isArray(detail)) {
    const parts = [];
    detail.forEach((item) => {
      if (!item || typeof item !== "object") return;
      const key = fieldName(item.loc);
      const msg = typeof item.msg === "string" ? item.msg : "";
      if (key) fields[key] = msg || genericFor(422, lang);
      const label = labels[key];
      if (label) parts.push(msg ? `${label}: ${msg}` : label);
      else if (msg) parts.push(msg);
    });
    const head = genericFor(422, lang);
    return { status, message: parts.length ? `${head} ${parts.join(" · ")}` : head, fields };
  }

  if (detail && typeof detail === "object") {
    const msg = detail.message || detail.error || detail.msg;
    return { status, message: typeof msg === "string" && msg.trim() ? msg.trim() : genericFor(status, lang), fields };
  }

  if (data && typeof data === "object" && typeof data.message === "string" && data.message.trim()) {
    return { status, message: data.message.trim(), fields };
  }

  return { status, message: genericFor(status, lang), fields };
};

/** Convenience helper: always a safe string, never an object. */
export const apiErrorMessage = (error, fallback) => {
  const parsed = parseApiError(error);
  return parsed.message || fallback || genericFor(parsed.status, currentLang());
};

/**
 * Installs the global guard. After this, `error.response.data.detail` is ALWAYS
 * a string, so the legacy `error.response?.data?.detail` call sites spread all
 * over the app can never hand React an object again.
 */
export const installApiErrorInterceptor = () => {
  if (window.__avisocarApiErrorInterceptor) return;
  window.__avisocarApiErrorInterceptor = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const parsed = parseApiError(error);
      error.avisocar = parsed;
      const data = error?.response?.data;
      if (data && typeof data === "object" && typeof data.detail !== "string") {
        try {
          data.detail = parsed.message;
          data.detail_fields = parsed.fields;
        } catch (_e) { /* frozen payload */ }
      }
      return Promise.reject(error);
    }
  );
};

export default parseApiError;
